import type { StampPointType } from '@/domain/stamp/useStampPointLabel'
import type { StampUnavailableReason, UserStampData } from '@/schemas/v2/user'

/**
 * 未マップのコードが落ちる汎用キー。API 文言へフォールバックするかの判定に使う（#3541）。
 */
export const ACQUIRE_ERROR_FALLBACK_KEY = 'spot_detail.acquire_error.message'

/**
 * スタンプ獲得 API のエラーコードを、エラーダイアログ文言の i18n キーに変換する。
 * 対応していないコードはデフォルトの汎用キーを返す。
 */
export const resolveAcquireErrorKey = (code: string | null, stampPointType: StampPointType = 'STAMP'): string => {
  switch (code) {
    case 'STAMP_ALREADY_ACQUIRED':
      if (stampPointType === 'POINT') return 'spot_detail.acquire_error.point_already_acquired'
      if (stampPointType === 'STAMP_POINT') return 'spot_detail.acquire_error.stamp_point_already_acquired'
      return 'spot_detail.acquire_error.stamp_already_acquired'
    case 'STAMP_ALREADY_ACQUIRED_TODAY':
      return 'spot_detail.acquire_error.stamp_already_acquired_today'
    case 'CAMPAIGN_PERIOD_OUT':
      return 'spot_detail.acquire_error.campaign_period_out'
    case 'STAMP_PERIOD_OUT':
      return 'spot_detail.acquire_error.stamp_period_out'
    case 'STAMP_DAY_OUT':
      return 'spot_detail.acquire_error.stamp_day_out'
    case 'STAMP_HOURS_OUT':
      return 'spot_detail.acquire_error.stamp_hours_out'
    case 'PERIOD_OUT':
      return 'spot_detail.acquire_error.period_out'
    case 'STAMP_NOT_AVAILABLE':
      return 'spot_detail.acquire_error.stamp_not_available'
    case 'SHEET_LOCKED':
      return 'spot_detail.acquire_error.sheet_locked'
    case 'STAMP_LOCK_FAILED':
      return 'spot_detail.acquire_error.stamp_lock_failed'
    // 位置情報が必要なのに座標を送れなかった（#3445 以降、座標未取得時は lat/lon を送らない）。
    // v1 から存置している「位置情報が取得できませんでした。」を流用する（#3541）
    case 'STAMP_LOCATION_REQUIRED':
      return 'spot.access_error_message_03'
    // 距離判定で弾かれた。押下時のスナックバー（gps_range_error）と同じ文言に揃える（#3541）
    case 'STAMP_RANGE_OUT':
    case 'STAMP_GPS_OUT_OF_RANGE':
      return stampPointType === 'POINT' ? 'spot_detail.gps_range_error.point' : 'spot_detail.gps_range_error.stamp'
    // ---- OCR 固有コード（#3694）----
    // ja_JP の値はバックエンド（CreateUserStampUseCase / OcrScanProcessor / ImageQualityChecker）の message と同一。
    // 読み取り失敗系（OCR_EXTRACT_* 等）は ocrReadFailure に行くためここには来ない。
    // バックエンドの message は多言語化されていないため raw 表示に頼らず全コードをここで翻訳する。
    // 重複・申請中
    case 'OCR_DUPLICATION':
      return 'spot_detail.acquire_error.ocr_duplication'
    case 'OCR_APPLICATION_PENDING':
      return 'spot_detail.acquire_error.ocr_application_pending'
    // 上限
    case 'OCR_DAILY_LIMIT_EXCEEDED':
      return 'spot_detail.acquire_error.ocr_daily_limit_exceeded'
    // 位置情報（POINT / STAMP の出し分けはしない。バックエンド文言に区別がないため）
    case 'OCR_LOCATION_REQUIRED':
      return 'spot_detail.acquire_error.ocr_location_required'
    case 'OCR_GPS_OUT_OF_RANGE':
      return 'spot_detail.acquire_error.ocr_gps_out_of_range'
    case 'OCR_SPOT_NO_LOCATION':
      return 'spot_detail.acquire_error.ocr_spot_no_location'
    // 設定・リクエスト不備
    case 'OCR_SPOT_ID_REQUIRED':
      return 'spot_detail.acquire_error.ocr_spot_id_required'
    case 'OCR_SPOT_MISMATCH':
      return 'spot_detail.acquire_error.ocr_spot_mismatch'
    case 'STAMP_OCR_CONFIG_NOT_FOUND':
      return 'spot_detail.acquire_error.ocr_config_not_found'
    case 'STAMP_IMAGE_REQUIRED':
      return 'spot_detail.acquire_error.ocr_image_required'
    // 画質・画像形式
    case 'OCR_IMAGE_TOO_LARGE':
      return 'spot_detail.acquire_error.ocr_image_too_large'
    case 'OCR_IMAGE_INVALID':
      return 'spot_detail.acquire_error.ocr_image_invalid'
    case 'OCR_IMAGE_TOO_SMALL':
      return 'spot_detail.acquire_error.ocr_image_too_small'
    case 'OCR_IMAGE_INVALID_FORMAT':
      return 'spot_detail.acquire_error.ocr_image_invalid_format'
    case 'OCR_IMAGE_RESOLUTION_TOO_LOW':
      return 'spot_detail.acquire_error.ocr_image_resolution_too_low'
    case 'OCR_IMAGE_TOO_DARK':
      return 'spot_detail.acquire_error.ocr_image_too_dark'
    case 'OCR_IMAGE_TOO_BRIGHT':
      return 'spot_detail.acquire_error.ocr_image_too_bright'
    case 'OCR_IMAGE_BLURRY':
      return 'spot_detail.acquire_error.ocr_image_blurry'
    case 'OCR_IMAGE_PROCESS_ERROR':
      return 'spot_detail.acquire_error.ocr_image_process_error'
    default:
      return ACQUIRE_ERROR_FALLBACK_KEY
  }
}

/**
 * スタンプ獲得エラーの表示内容。
 * i18n キーで出すか、API が返した文言をそのまま出すかを表す。
 */
export type AcquireErrorDisplay = { type: 'i18n'; key: string } | { type: 'raw'; message: string }

/**
 * スタンプ獲得エラーの表示内容を決める（#3541）。
 *
 * i18n キーがあるコードは翻訳を優先し（全対応言語での表示を維持するため）、
 * 未マップのコードは API が返した利用者向け文言をそのまま出す。
 * これによりフロント側で全エラーコードを列挙し続けるハードコーディングをやめる。
 *
 * `apiMessage` には `extractApiErrorMessage()` の戻り値を渡すこと。
 * 同関数はバックエンドが公開不可として差し込む汎用文言を null に落とすため、
 * その場合は情報量の多いフロントの汎用文言（{{what}} 入り）にフォールバックできる。
 */
export const resolveAcquireErrorDisplay = (
  code: string | null,
  apiMessage: string | null,
  stampPointType: StampPointType = 'STAMP',
): AcquireErrorDisplay => {
  const key = resolveAcquireErrorKey(code, stampPointType)
  if (key !== ACQUIRE_ERROR_FALLBACK_KEY) return { type: 'i18n', key }
  if (apiMessage != null && apiMessage !== '') return { type: 'raw', message: apiMessage }
  return { type: 'i18n', key }
}

/**
 * 全スタンプが獲得不可のときに表示する理由の優先順位（先頭ほど優先）。
 *
 * 1つのスポットに複数スタンプが紐づく場合（#2540）、理由が混在し得る
 * （例: スタンプA=獲得済み / スタンプB=期間外）。
 * 「1件でも獲得済みなら獲得済みと表示する」ため、獲得済み系を先に置く。
 * 通算の獲得済みを表す STAMP_ALREADY_ACQUIRED を、リピートスタンプの1日1回制限より上位にする。
 */
const UNAVAILABLE_REASON_PRIORITY: StampUnavailableReason[] = ['STAMP_ALREADY_ACQUIRED', 'STAMP_ALREADY_ACQUIRED_TODAY']

/**
 * 取得済みユーザースタンプ群から、スタンプ獲得ボタンを非活性にすべき理由を決める。
 * 獲得可能（＝ボタン活性）なら null を返す。
 *
 * - 1件でも獲得可能なものがあれば活性のまま（1スポットに複数スタンプが紐づくケースに対応）。
 * - 全件獲得不可のときは UNAVAILABLE_REASON_PRIORITY を優先し、該当がなければ
 *   渡された順（`spot.stamps` の startAt 昇順）で最初に見つかった理由を返す。
 * - `isAvailable=false` なのに理由が null になるのは、バックエンドが追加した未知コードを
 *   スキーマの `catch(null)` が縮退させたケース。理由不明でも押せないことは確かなので
 *   汎用文言に倒し、ボタンを活性のままにしない。
 */
export const resolveStampsUnavailableReason = (
  stamps: Pick<UserStampData, 'isAvailable' | 'unavailableReason'>[],
): StampUnavailableReason | null => {
  if (stamps.length === 0) return null
  if (!stamps.every((s) => !s.isAvailable)) return null

  const reasons = stamps.map((s) => s.unavailableReason).filter((r) => r != null)
  for (const priority of UNAVAILABLE_REASON_PRIORITY) {
    if (reasons.includes(priority)) return priority
  }
  return reasons[0] ?? 'STAMP_NOT_AVAILABLE'
}

/**
 * `UserStampData.unavailableReason` を、スタンプ獲得ボタンのラベル i18n キーに変換する。
 *
 * 曜日・祝日条件（STAMP_DAY_OUT）や1日1回制限（STAMP_ALREADY_ACQUIRED_TODAY）は
 * クライアント側で判定できないため、サーバーが返した理由をそのまま文言に反映する。
 */
export const resolveStampUnavailableLabelKey = (
  reason: StampUnavailableReason,
  stampPointType: StampPointType = 'STAMP',
): string => {
  switch (reason) {
    case 'STAMP_ALREADY_ACQUIRED':
      return stampPointType === 'POINT' ? 'spot_detail.point_acquired' : 'spot_detail.stamp_acquired'
    case 'STAMP_ALREADY_ACQUIRED_TODAY':
      return 'spot_detail.stamp_unavailable.already_acquired_today'
    case 'CAMPAIGN_PERIOD_OUT':
      return 'spot_detail.stamp_unavailable.campaign_period_out'
    case 'STAMP_PERIOD_OUT':
      return stampPointType === 'POINT' ? 'spot_detail.point_out_of_period' : 'spot_detail.stamp_out_of_period'
    case 'STAMP_DAY_OUT':
      return 'spot_detail.stamp_unavailable.day_out'
    case 'STAMP_HOURS_OUT':
      return 'spot_detail.stamp_unavailable.hours_out'
    // シート非表示・購入連動ロックはユーザーに詳細を伝えても行動できないため汎用文言にまとめる
    case 'STAMP_NOT_AVAILABLE':
    case 'SHEET_LOCKED':
      return 'spot_detail.stamp_unavailable.not_available'
  }
}
